import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Copy, RefreshCw, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

/**
 * PhoneCameraPanel — host-side drawer shown inside DirectorConsole.
 *
 * Generates a pair token + QR code.  When the phone joins, this panel
 * creates an RTCPeerConnection, accepts the offer, and fires an
 * `onStream(stream)` callback so the consumer can mount the video as an
 * additional switcher source.
 */
const STUN = [{ urls: "stun:stun.l.google.com:19302" }];

export default function PhoneCameraPanel({ onStream, onEnded }) {
  const [pair, setPair] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | awaiting | connected
  const pcRef = useRef(null);
  const wsRef = useRef(null);

  const pairUrl = pair ? `${window.location.origin}/phone/camera/${pair.token}` : null;
  const qrSrc = pairUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(pairUrl)}`
    : null;

  const create = async () => {
    try {
      const { data } = await api.post("/phone-camera/pair/create");
      setPair(data); setStatus("awaiting");
      // Open host-side WS to receive the phone's offer
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
      const tok = localStorage.getItem("tv_token") || "";
      const ws = new WebSocket(`${base}/api/phone-camera/ws/${data.token}?role=host&auth_token=${encodeURIComponent(tok)}`);
      wsRef.current = ws;

      const pc = new RTCPeerConnection({ iceServers: STUN });
      pcRef.current = pc;
      pc.onicecandidate = (e) => {
        if (e.candidate && ws.readyState === 1) ws.send(JSON.stringify({ type: "ice", candidate: e.candidate }));
      };
      pc.ontrack = (ev) => {
        if (ev.streams && ev.streams[0]) {
          onStream?.(ev.streams[0]);
          setStatus("connected");
        }
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "failed" || pc.connectionState === "disconnected") {
          setStatus("idle"); onEnded?.();
        }
      };

      ws.onmessage = async (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m.type === "offer" && m.sdp) {
            await pc.setRemoteDescription(new RTCSessionDescription(m.sdp));
            const ans = await pc.createAnswer();
            await pc.setLocalDescription(ans);
            ws.send(JSON.stringify({ type: "answer", sdp: pc.localDescription }));
          } else if (m.type === "ice" && m.candidate) {
            try { await pc.addIceCandidate(new RTCIceCandidate(m.candidate)); } catch {}
          } else if (m.type === "peer_left") {
            setStatus("idle"); onEnded?.();
          }
        } catch {}
      };
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const end = () => {
    try { wsRef.current?.close?.(); } catch {}
    try { pcRef.current?.close?.(); } catch {}
    wsRef.current = null; pcRef.current = null;
    setPair(null); setStatus("idle"); onEnded?.();
  };

  useEffect(() => () => end(), []);

  return (
    <div data-testid="phone-camera-panel" className="rounded-lg border border-white/10 bg-black/30 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Camera className="w-3.5 h-3.5 text-sky-400" />
        <div className="text-white font-black text-[11px] uppercase tracking-wider flex-1">Phone camera</div>
        {status === "connected" && <span className="inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 text-[9px] font-black px-2 py-0.5 rounded-full">LIVE</span>}
      </div>

      {!pair && (
        <>
          <div className="text-white/60 text-[10px]">Pair your phone as an extra camera — grabs camera + mic and streams to your console over WebRTC.</div>
          <button
            data-testid="phone-cam-create"
            onClick={create}
            className="w-full bg-sky-500 hover:bg-sky-400 text-black font-black text-[11px] py-1.5 rounded"
          >
            Generate pairing QR
          </button>
        </>
      )}

      {pair && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 bg-white/5 rounded p-2">
            {qrSrc && <img src={qrSrc} alt="Pair QR" className="w-24 h-24 rounded bg-white p-1 shrink-0" />}
            <div className="flex-1 min-w-0 space-y-1">
              <div className="text-white text-[10px] font-bold">Scan on your phone</div>
              <div className="text-white/60 text-[9px]">Or open this link:</div>
              <code className="block text-[9px] font-mono text-white bg-black/50 rounded px-1.5 py-1 truncate" data-testid="phone-cam-url">{pairUrl}</code>
              <div className="flex gap-1">
                <button
                  data-testid="phone-cam-copy"
                  onClick={() => { navigator.clipboard.writeText(pairUrl); toast.success("Link copied"); }}
                  className="flex-1 bg-fuchsia-500 hover:bg-fuchsia-400 text-black font-black text-[9px] py-1 rounded inline-flex items-center justify-center gap-1"
                >
                  <Copy className="w-2.5 h-2.5" /> Copy
                </button>
                <button
                  data-testid="phone-cam-regen"
                  onClick={() => { end(); create(); }}
                  className="bg-white/10 hover:bg-white/20 text-white text-[9px] font-black px-2 py-1 rounded inline-flex items-center gap-1"
                  title="Regenerate pair token"
                ><RefreshCw className="w-2.5 h-2.5" /></button>
                <button
                  data-testid="phone-cam-end"
                  onClick={end}
                  className="bg-red-500/20 hover:bg-red-500/40 text-red-200 text-[9px] font-black px-2 py-1 rounded inline-flex items-center gap-1"
                ><X className="w-2.5 h-2.5" /></button>
              </div>
            </div>
          </div>
          <div className="text-white/40 text-[9px] italic">
            {status === "awaiting" ? "Waiting for phone to connect…" : status === "connected" ? "Phone camera connected — add it to your switcher." : ""}
          </div>
        </div>
      )}
    </div>
  );
}
